import React, { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { formatCurrency, formatDate } from "../lib/format";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowUpRight,
  Mic,
  Users,
  FileText,
  Receipt,
} from "lucide-react";
import Model303Widget from "../components/Model303Widget";
import ExpenseDashboard from "../components/ExpenseDashboard";
import ProfitWidget from "../components/ProfitWidget";
import TopSuppliersWidget from "../components/TopSuppliersWidget";
import ExpenseCategoryWidget from "../components/ExpenseCategoryWidget";

const Kpi = ({ icon: Icon, label, value, accent = "blue", testid }) => {
  const palette = {
    blue: "from-blue-500/20 to-blue-500/0 text-blue-400 border-blue-500/30",
    emerald: "from-emerald-500/20 to-emerald-500/0 text-emerald-400 border-emerald-500/30",
    amber: "from-amber-500/20 to-amber-500/0 text-amber-400 border-amber-500/30",
    rose: "from-rose-500/20 to-rose-500/0 text-rose-400 border-rose-500/30",
  }[accent];
  return (
    <div className="card-surface p-5 relative overflow-hidden group hover:border-blue-500/30 transition-all" data-testid={testid}>
      <div className={`absolute inset-0 bg-gradient-to-br ${palette} opacity-30`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-9 h-9 rounded-lg border ${palette} flex items-center justify-center`}>
            <Icon size={16} />
          </div>
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
        <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">
          {value}
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { openVoice } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [clientsMap, setClientsMap] = useState({});

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/clients").then((r) => {
      const m = {};
      r.data.forEach((c) => (m[c.id] = c));
      setClientsMap(m);
    });
  }, []);

  if (!stats) {
    return <div className="text-slate-400">{t("common.loading")}</div>;
  }

  const k = stats.kpi;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-sm text-slate-400">
            {t("dashboard.welcome")}, <span className="text-white font-medium">{user.full_name}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter mt-1">
            {t("dashboard.title")}
          </h1>
        </div>
        <button
          type="button"
          onClick={openVoice}
          data-testid="dashboard-voice-cta"
          className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white px-5 py-2.5 rounded-lg font-medium shadow-[0_0_24px_rgba(59,130,246,0.35)]"
        >
          <Mic size={16} />
          {t("dashboard.voice_cta")}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          icon={TrendingUp}
          label={t("dashboard.revenue_month")}
          value={formatCurrency(k.revenue_month, lang)}
          accent="blue"
          testid="kpi-revenue"
        />
        <Kpi
          icon={Clock}
          label={t("dashboard.pending")}
          value={formatCurrency(k.pending_total, lang)}
          accent="amber"
          testid="kpi-pending"
        />
        <Kpi
          icon={CheckCircle2}
          label={t("dashboard.paid")}
          value={formatCurrency(k.paid_total, lang)}
          accent="emerald"
          testid="kpi-paid"
        />
        <Kpi
          icon={AlertCircle}
          label={t("dashboard.overdue")}
          value={formatCurrency(k.overdue_total, lang)}
          accent="rose"
          testid="kpi-overdue"
        />
      </div>

      {/* Profit (income vs expense) widget */}
      <ProfitWidget />

      {/* Model 303 widget */}
      <Model303Widget />

      {/* Expenses (received invoices) dashboard */}
      <ExpenseDashboard />

      {/* Top suppliers ranking + category breakdown side-by-side on large screens */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <TopSuppliersWidget />
        <ExpenseCategoryWidget />
      </div>

      {/* Chart + Sidebar */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">
              {t("dashboard.monthly_revenue")}
            </h2>
            <div className="text-xs text-slate-500">
              {k.invoices_count} {t("dashboard.invoices").toLowerCase()}
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={stats.monthly_revenue}
                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis
                  dataKey="month"
                  stroke="#64748B"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => v.slice(5)}
                />
                <YAxis stroke="#64748B" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: "rgba(11,15,25,0.95)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "8px",
                    fontSize: 12,
                  }}
                  formatter={(v) => formatCurrency(v, lang)}
                  cursor={{ stroke: "#3B82F6", strokeWidth: 1 }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#60A5FA"
                  strokeWidth={2.5}
                  fill="url(#rev)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-6">
          <h2 className="font-display text-lg font-semibold mb-4">
            {t("dashboard.upcoming")}
          </h2>
          {stats.upcoming.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">
              {t("dashboard.no_data")}
            </div>
          ) : (
            <div className="space-y-3">
              {stats.upcoming.map((u) => (
                <Link
                  key={u.id}
                  to={`/app/invoices/${u.id}`}
                  className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 hover:bg-white/5 -mx-2 px-2 rounded transition-all"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{u.number}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {clientsMap[u.client_id]?.name || "—"} · {formatDate(u.due_date, lang)}
                    </div>
                  </div>
                  <div className={`text-sm font-semibold ${u.days < 0 ? "text-rose-400" : u.days <= 3 ? "text-amber-400" : "text-slate-200"}`}>
                    {formatCurrency(u.total, lang)}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick-glance */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Link to="/app/clients" className="card-surface p-5 flex items-center justify-between hover:border-blue-500/30 transition-all">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">{t("dashboard.clients")}</div>
            <div className="font-display text-2xl font-bold mt-1">{k.clients_count}</div>
          </div>
          <Users className="text-blue-400" size={24} />
        </Link>
        <Link to="/app/quotes" className="card-surface p-5 flex items-center justify-between hover:border-blue-500/30 transition-all">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">{t("dashboard.quotes")}</div>
            <div className="font-display text-2xl font-bold mt-1">{k.quotes_count}</div>
            <div className="text-xs text-slate-500 mt-1">
              {stats.quote_stats.accepted} acceptats · {stats.quote_stats.sent} enviats
            </div>
          </div>
          <FileText className="text-blue-400" size={24} />
        </Link>
        <Link to="/app/invoices" className="card-surface p-5 flex items-center justify-between hover:border-blue-500/30 transition-all">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">{t("dashboard.invoices")}</div>
            <div className="font-display text-2xl font-bold mt-1">{k.invoices_count}</div>
            <div className="text-xs text-slate-500 mt-1">
              {k.paid_count} cobrades · {k.pending_count} pendents
            </div>
          </div>
          <Receipt className="text-blue-400" size={24} />
        </Link>
      </div>

      {/* Recent activity */}
      <div className="card-surface p-6">
        <h2 className="font-display text-lg font-semibold mb-4">
          {t("dashboard.recent_activity")}
        </h2>
        {stats.recent_activity.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            {t("dashboard.no_data")}
          </div>
        ) : (
          <div className="space-y-2">
            {stats.recent_activity.slice(0, 8).map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm py-2 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                    <ArrowUpRight size={13} className="text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-slate-200">
                      <span className="font-medium">{a.action}</span> · {a.entity_type}
                      {a.detail ? <span className="text-slate-500"> · {a.detail}</span> : null}
                    </div>
                  </div>
                </div>
                <span className="text-xs text-slate-500 whitespace-nowrap">{formatDate(a.timestamp, lang)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
